import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { getUser, updateUser } from "../../actions/userActions";
import { clearError, clearUserUpdated } from "../../slices/authSlice";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../Loader";
import { MdSecurity, MdPerson, MdEmail, MdSave, MdArrowBack } from "react-icons/md";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const UpdateUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        user = {},
        loading = true,
        error,
        isUserUpdated,
    } = useSelector((state) => state.authState);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("");

    useEffect(() => {
        if (user && user._id !== id) {
            dispatch(getUser(id));
        } else {
            setName(user.name);
            setEmail(user.email);
            setRole(user.role);
        }

        if (error) {
            toast.error(error, {
                position: toast.POSITION.BOTTOM_CENTER,
                onOpen: () => dispatch(clearError()),
            });
        }

        if (isUserUpdated) {
            toast.success("User role updated successfully!", {
                position: toast.POSITION.BOTTOM_CENTER,
            });
            dispatch(clearUserUpdated());
            navigate("/admin/users");
        }
    }, [dispatch, error, isUserUpdated, user, id, navigate]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const userData = { name, email, role };
        dispatch(updateUser(id, userData));
    };

    const inputClasses = "w-full bg-white text-gray-900 px-6 py-4 rounded-2xl border border-gray-100 transition-all duration-300 outline-none text-sm font-medium focus:border-yellow-600 focus:shadow-[0_0_15px_rgba(212,175,55,0.1)]";

    return (
        <div className="flex bg-gray-50 min-h-screen">
            <Sidebar />
            
            <div className="flex-1 p-8 lg:p-12 overflow-x-hidden">
                {loading ? (
                    <Loader />
                ) : (
                    <div className="max-w-2xl mx-auto">
                        {/* Header */}
                        <div className="mb-12">
                            <Link to="/admin/users" className="inline-flex items-center text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6 hover:text-yellow-600 transition-colors">
                                <MdArrowBack className="mr-2 text-sm" /> Back to Directory
                            </Link>
                            <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2">Edit Team Member</h1>
                            <p className="text-gray-500 text-sm font-light">Update permissions and access levels for this personnel account.</p>
                        </div>

                        <div className="bg-white rounded-[2.5rem] p-10 border border-gray-100 shadow-sm relative overflow-hidden">
                            {/* Decorative elements */}
                            <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-50 rounded-full -mr-16 -mt-16 opacity-50"></div>
                            
                            <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                                <div className="text-center mb-10">
                                    <div className="w-20 h-20 bg-yellow-50 rounded-full flex items-center justify-center text-yellow-600 mx-auto mb-4">
                                        <MdSecurity className="text-3xl" />
                                    </div>
                                    <h2 className="text-xs font-bold tracking-[.3em] text-yellow-600 uppercase">Permission Settings</h2>
                                </div>

                                {/* Name (Read-only aesthetic) */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Account Holder</label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className={inputClasses}
                                            placeholder="User Name"
                                        />
                                        <MdPerson className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email Connection</label>
                                    <div className="relative group">
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className={inputClasses}
                                            placeholder="User Email"
                                        />
                                        <MdEmail className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                                    </div>
                                </div>

                                {/* Role Selection */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Security Role</label>
                                    <div className="relative group">
                                        <select
                                            value={role}
                                            onChange={(e) => setRole(e.target.value)}
                                            className={`${inputClasses} appearance-none cursor-pointer`}
                                        >
                                            <option value="user">Client (User)</option>
                                            <option value="admin">Curator (Admin)</option>
                                        </select>
                                        <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <div className="w-2 h-2 border-r-2 border-b-2 border-gray-300 rotate-45"></div>
                                        </div>
                                    </div>
                                    <p className="text-[9px] text-gray-400 font-medium italic mt-2 ml-2">Note: Elevating a user to Admin grants full access to inventory and financials.</p>
                                </div>

                                <div className="pt-8">
                                    <button
                                        type="submit"
                                        className="w-full bg-gray-900 text-white px-16 py-5 rounded-2xl font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-yellow-600 transition-all shadow-[0_20px_50px_rgba(0,0,0,0.1)] hover:shadow-yellow-600/20 active:scale-95 flex items-center justify-center gap-3"
                                    >
                                        <MdSave className="text-base" /> Apply Security Changes
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpdateUser;
